import { useEffect, useMemo, useState } from 'react';
import { formatTime } from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import { Banner, Button, Dialog, FilterChip, Skeleton, TextField, Textarea } from '../../ds/components';
import { ApiError, errorMessage } from '../../lib/api';
import { usePermissions } from '../../lib/session';
import { addDays, longDay, todayKey } from '../tasks/dates';
import { useReschedule, useSlots, type AppointmentDetail, type ConflictDetails, type Slot } from './api';
import styles from './RescheduleDialog.module.css';

export interface RescheduleDialogProps {
  open: boolean;
  appointment: AppointmentDetail;
  onClose: () => void;
  onRescheduled: (created: AppointmentDetail) => void;
}

function conflictDetails(error: unknown): ConflictDetails | null {
  if (!(error instanceof ApiError) || error.code !== 'CONFLICT') return null;
  const details = error.details as Partial<ConflictDetails> | undefined;
  if (!details || !Array.isArray(details.conflicts)) return null;
  return {
    conflicts: details.conflicts,
    alternatives: Array.isArray(details.alternatives) ? details.alternatives : [],
    dayOverview: Array.isArray(details.dayOverview) ? details.dayOverview : [],
    requested: details.requested ?? { startsAt: '', endsAt: '', advisorId: '' },
  };
}

/**
 * „Verschieben" (Q-08, A18): pick a day, choose one of the advisor's free slots (`GET /slots?internal=1`),
 * give an optional reason → `POST /appointments/:id/reschedule`. A conflict lists the colliding entries
 * and offers the server's alternatives; admins may override.
 */
export function RescheduleDialog({ open, appointment, onClose, onRescheduled }: RescheduleDialogProps) {
  const permissions = usePermissions();
  const reschedule = useReschedule();
  const today = todayKey();
  const [day, setDay] = useState(today);
  const [startsAt, setStartsAt] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [missing, setMissing] = useState(false);
  const [override, setOverride] = useState(false);
  const resetMutation = reschedule.reset;

  useEffect(() => {
    if (!open) return;
    setDay(todayKey());
    setStartsAt(null);
    setReason('');
    setMissing(false);
    setOverride(false);
    resetMutation();
  }, [open, resetMutation]);

  const validDay = /^\d{4}-\d{2}-\d{2}$/.test(day) && day >= today;
  const slots = useSlots(
    open && validDay
      ? {
          eventTypeId: appointment.eventType.id,
          from: day,
          to: addDays(day, 1),
          advisorId: appointment.advisor.id,
        }
      : null,
  );
  const daySlots = useMemo<Slot[]>(
    () => slots.data?.days.find((entry) => entry.date === day)?.slots ?? [],
    [slots.data, day],
  );
  const conflict = conflictDetails(reschedule.error);
  const canOverride = permissions.is('settings.organization', 'yes');

  function submit(overrideConflict = false) {
    if (startsAt === null) {
      setMissing(true);
      return;
    }
    setMissing(false);
    reschedule.mutate(
      {
        id: appointment.id,
        body: {
          startsAt,
          ...(reason.trim() !== '' ? { reason: reason.trim() } : {}),
          ...(overrideConflict ? { overrideConflict: true } : {}),
        },
      },
      { onSuccess: onRescheduled },
    );
  }

  const genericError = reschedule.isError && conflict === null ? errorMessage(reschedule.error) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={600}
      eyebrow={appointment.eventType.name}
      title="Termin verschieben"
      subtitle={`Bisher ${appointment.timeRangeText}`}
      footerNote="Der Kontakt bekommt eine neue Bestätigung mit ICS."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={reschedule.isPending}>
            Abbrechen
          </Button>
          {conflict && canOverride ? (
            <Button
              variant="secondary"
              loading={reschedule.isPending && override}
              onClick={() => {
                setOverride(true);
                submit(true);
              }}
            >
              Trotzdem anlegen
            </Button>
          ) : null}
          <Button loading={reschedule.isPending && !override} onClick={() => submit(false)}>
            Verschieben
          </Button>
        </>
      }
    >
      <div className={styles.body}>
        {genericError ? <Banner tone="danger">{genericError}</Banner> : null}
        {conflict ? (
          <Banner tone="warning" title="Terminkonflikt">
            {conflict.conflicts.map((entry) => (
              <div key={`${entry.kind}-${entry.startsAt}`}>
                {entry.title} · {formatTime(entry.startsAt)}–{formatTime(entry.endsAt)}
              </div>
            ))}
            {conflict.alternatives.length > 0 ? (
              <div className={styles.alternatives}>
                <span className={styles.alternativesLabel}>Nächste freie Zeiten:</span>
                {conflict.alternatives.map((slot) => (
                  <FilterChip
                    key={slot.startsAt}
                    label={`${longDay(slot.startsAt.slice(0, 10))} ${formatTime(slot.startsAt)}`}
                    active={startsAt === slot.startsAt}
                    onClick={() => {
                      setStartsAt(slot.startsAt);
                      setDay(slot.startsAt.slice(0, 10));
                    }}
                  />
                ))}
              </div>
            ) : null}
            {!canOverride ? (
              <div className={styles.helper}>„Trotzdem anlegen“ ist Admins vorbehalten.</div>
            ) : null}
          </Banner>
        ) : null}
        <TextField
          label="Neuer Tag"
          type="date"
          required
          min={today}
          value={day}
          onChange={(event) => {
            setDay(event.target.value);
            setStartsAt(null);
          }}
        />
        <div className={styles.slotsLabel}>
          Freie Zeiten · {appointment.advisor.shortName}
          {missing ? <span className={styles.slotsError}> — Bitte eine Zeit wählen.</span> : null}
        </div>
        {!validDay ? (
          <div className={styles.slotsEmpty}>Bitte einen Tag ab heute wählen.</div>
        ) : slots.isPending ? (
          <div className={styles.slots} aria-busy="true">
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <Skeleton key={index} width={64} height={30} />
            ))}
          </div>
        ) : slots.isError ? (
          <Banner tone="danger">{errorMessage(slots.error)}</Banner>
        ) : daySlots.length === 0 ? (
          <div className={styles.slotsEmpty}>An diesem Tag ist keine Zeit frei.</div>
        ) : (
          <div className={styles.slots} role="radiogroup" aria-label="Freie Zeiten">
            {daySlots.map((slot) => (
              <FilterChip
                key={slot.startsAt}
                role="radio"
                aria-checked={startsAt === slot.startsAt}
                label={formatTime(slot.startsAt)}
                active={startsAt === slot.startsAt}
                className={clsx(styles.slot)}
                onClick={() => setStartsAt(slot.startsAt)}
              />
            ))}
          </div>
        )}
        <Textarea
          label="Grund"
          optional
          rows={2}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </div>
    </Dialog>
  );
}
